import { useCallback } from 'react';
import { useGetUserPermissionsPerCompany } from 'api/services/userService';
import { useUI } from 'contexts/UiContext';
import { extractQueryData } from 'utils/services';
import { PermissionActions, PermissionModules } from 'types/rolesTypes';

/**
 * Hook that returns a permission-checking helper for the currently selected company.
 *
 * Internally it performs a single permissions query and memoises a `has` function
 * that can be invoked multiple times without re-triggering additional network requests.
 *
 * @example
 * ```tsx
 * const has = usePermissionChecker();
 * if (has(PermissionModules.Users, PermissionActions.Delete)) {
 *   // user can delete
 * }
 * ```
 *
 * The helper will return:
 *  • `true`  – when the user has the permission.
 *  • `false` – when the user does NOT have the permission.
 *  • `null`  – while the permissions are still loading or an error occurred.
 *
 * @returns {(module: PermissionModules, action: PermissionActions) => boolean | null}
 * A stable callback that can be used throughout the component lifecycle.
 */
export const usePermissionChecker = () => {
  const { selectedCompany } = useUI();

  // Fetch all permissions for the selected company only once.
  const userPermissionsQuery = useGetUserPermissionsPerCompany(
    selectedCompany ?? ''
  );
  const permissionsData = extractQueryData(userPermissionsQuery, undefined);

  const has = useCallback(
    (module: PermissionModules, action: PermissionActions): boolean | null => {
      if (
        userPermissionsQuery.isLoading ||
        userPermissionsQuery.isError ||
        !permissionsData
      )
        return null;
      return (permissionsData[module] ?? []).includes(action);
    },
    [userPermissionsQuery.isLoading, userPermissionsQuery.isError, permissionsData]
  );

  return has;
};
